
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react'
import AddPaymentPopup from 'Popups/AddPaymentPopup/AddPaymentPopup';
import AddPatientPaymentPopup from 'Popups/AddPatientPaymentPopup/AddPatientPaymentPopup';
import SlidingTabs from 'Common/SlidingTabs/SlidingTabs';
import PaymentPlans from './PaymentPlans';
import Subscriptions from './Subscriptions';
import Show from 'Components/Show/Show';
import { ErrorHandlingMessage } from 'Utils/ErrorHandlingMessage';
import api from 'http';
import ParamListGen from 'Services/ParamListGen';
import CreatePaymentPlanPopup from 'Popups/CreatePaymentPlanPopup/CreatePaymentPlanPopup';
import CustomButtonNew from 'Common/CustomButtonNew/CustomButtonNew';
import { TfiReload } from "react-icons/tfi";
import { Tooltip } from 'react-tooltip';

export const recurrentPaymentSubTabs = {
  "Payment Plans": "payment plans",
  "Subscriptions": "subscriptions",
};

const RecurrentPaymentSearch = forwardRef(({ 
  handlePaymentId, 
  searchModel, 
  setSearchModel,
  loader,
  setLoader,
  calculateFilterCount,
  initialSearchModel
}, ref) => {
  const [paymentPopup, setPaymentPopup] = useState(false)
  const [state, setState] = useState({
    modalRow: null,
    showPaymentDetail: false,
  })
  const [activeSubTab, setActiveSubTab] = useState(recurrentPaymentSubTabs["Payment Plans"]);
  const [allPlans, setAllPlans] = useState([]);
  const [totalrecord, setTotalrecord] = useState(10);
  const [paymentPlanPopup, setPaymentPlanPopup] = useState({
    status: false,
    editingPlan: null
  });

  const handleSubTabChange = (tab) => {
    const tabKey = recurrentPaymentSubTabs[tab];
    setActiveSubTab(tabKey);
    setTotalrecord(10);
  }

  const handleSubmit = async (loadMore = false, customModel = null) => {
    try {
      setLoader(true);
      const currentTotalRecord = loadMore ? totalrecord + 10 : 10;
      const planTypeValue = activeSubTab === recurrentPaymentSubTabs["Payment Plans"] ? "BAL" : "SUB";
      const modelToUse = customModel || searchModel;
      const payload = {
        ...modelToUse,
        planType: planTypeValue,
        totalrecord: currentTotalRecord
      };
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === '') {
          payload[key] = null;
        }
      });
      const response = await api.post(
        `${process.env.REACT_APP_TC_PMS}/api/patientpayment/v2/FindRecurringPaymentPlans`,
        payload
      );
      if (loadMore) {
        setAllPlans(response.data.dataModel || []);
        setTotalrecord(currentTotalRecord);
      } else {
        setAllPlans(response.data.dataModel || []);
        setTotalrecord(10);
      }
      
      if (calculateFilterCount && initialSearchModel) {
        const paramList = ParamListGen(modelToUse);
        calculateFilterCount(paramList, initialSearchModel, 'planType');
      }
    } catch (error) {
      ErrorHandlingMessage(error);
    } finally {
      setLoader(false);
    }
  };


  const handleLoadMore = () => {
    handleSubmit(true);
  };

  const handleReload = () => {
    handleSubmit(false);
  };

  const handleClear = () => {
    setTotalrecord(10);
    setAllPlans([]);
  };

  const handleClosePaymentPopup = () => {
    setPaymentPopup(false);
  };

  const handleClosePaymentDetail = () => {
    setState({ ...state, modalRow: null, showPaymentDetail: false });
  };

  const handleOpenPlanPopup = () => {
    setPaymentPlanPopup((prev) => ({ ...prev, status: true }));
  };

  const handleClosePlanPopup = () => {
    setPaymentPlanPopup({
      status: false,
      editingPlan: null
    });
    // Reload data after closing popup to show any new/updated plans
    handleReload();
  };

  const handleEditPlan = (plan) => {
    setPaymentPlanPopup({
      status: true,
      editingPlan: plan
    });
  };

  const handleReloadPayments = () => {
    handleReload();
  };

  useImperativeHandle(ref, () => ({
    handleSubmit,
    handleClear,
    activeSubTab
  }));

  useEffect(() => {
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  const handleTabClick = (tab) => {
    handleSubTabChange(tab);
  };

  const isPaymentPlansTab = activeSubTab === recurrentPaymentSubTabs["Payment Plans"];
  const isSubscriptionsTab = activeSubTab === recurrentPaymentSubTabs["Subscriptions"];

  return (
    <div className=''>
      <Show when={paymentPopup}>
        <AddPaymentPopup onClose={handleClosePaymentPopup} />
      </Show>
      
      <Show when={state.showPaymentDetail}>
        <AddPatientPaymentPopup modalRow={state.modalRow} onClose={handleClosePaymentDetail} />
      </Show>

      <Show when={paymentPlanPopup.status}>
        <CreatePaymentPlanPopup onClose={handleClosePlanPopup} editingPlan={paymentPlanPopup.editingPlan} patientId={null} activeSubTab={activeSubTab} showPatientSearch={true} />
      </Show>

      <div className="flex items-center justify-between mb-4">
        <SlidingTabs activeTab={activeSubTab} extraClass="flex-1">
          {Object.keys(recurrentPaymentSubTabs).map((tab, index) => {
            const tabKey = recurrentPaymentSubTabs[tab];
            const isActive = activeSubTab === tabKey;
            const handleClick = () => handleTabClick(tab);
            return (
              <button 
                key={index} 
                value={tabKey}
                className={`text-md cursor-pointer pb-2 ${isActive ? "text-primary" : "text-secondaryLight"}`}
                onClick={handleClick}
              >
                {tab}
              </button>
            );
          })}
        </SlidingTabs>
        <div className='flex items-center gap-5 pb-2'>
          <Tooltip id="RecurrentPayments-tooltip" style={{ zIndex: 11 }} />
          <TfiReload 
            data-tooltip-id="RecurrentPayments-tooltip"
            data-tooltip-content={"Refresh the payments"} 
            onClick={handleReloadPayments} 
            size={20} 
            className='text-gray-400 outline-none cursor-pointer hover:animate-spin' 
          />
          <CustomButtonNew onClick={handleOpenPlanPopup} label="Create Payment Plan" />
        </div>
      </div>

      <Show when={isPaymentPlansTab}>
        <PaymentPlans 
          allPlans={allPlans}
          loader={loader}
          handleLoadMore={handleLoadMore}
          totalrecord={totalrecord}
          handleReload={handleReload}
          handleEditPlan={handleEditPlan}
        />
      </Show>

      <Show when={isSubscriptionsTab}>
        <Subscriptions 
          allPlans={allPlans}
          loader={loader}
          handleLoadMore={handleLoadMore}
          totalrecord={totalrecord}
          handleReload={handleReload}
          handleEditPlan={handleEditPlan}
        />
      </Show>
    </div>
  )
});

RecurrentPaymentSearch.displayName = 'RecurrentPaymentSearch';

export default RecurrentPaymentSearch;

